// tosDatosPersonales.js
//
// Sección 3 ("Datos personales y privacidad") de los dos Términos de Uso
// (Tipo A, tosContent.js, y Tipo C, tosContentInterno.js). Vive en un solo
// archivo para que los dos documentos no puedan decir cosas distintas sobre
// datos personales.
//
// Fuente: la Política de Privacidad mínima v2.0 (Planner 347; archivo
// proyectos_extra/operacional/auditoria_turno_2026-09/55_politica_minima/
// privacidad_minima.html, copiada en test-fixtures/privacidad_minima.html).
// Regla de redacción (Sebastián, 05-oct-2026, memoria
// feedback_no_publicar_en_contra): en lo que se superponen, los Términos dicen
// LO MISMO que la política, con sus mismas fórmulas; nada falso y nada que
// confiese una brecha. Lo interno (estado de cada contrato, defectos técnicos,
// copias) va al expediente, no a este texto.
//
// Si la política cambia, este archivo cambia con ella: el test
// tosPoliticaCoherencia.test.js fija el hash de la copia y compara proveedor por
// proveedor el "para qué" de esta lista contra la tabla del punto 4.

export const PRIVACIDAD_EMAIL = "privacidad@smardx.com";
export const POLITICA_URL = "https://smardx.com/privacidad.html";

// Misma lista, mismo orden y mismo "para qué" que la tabla del punto 4 de la
// política. Las referencias "(punto N)" de la política se escriben acá
// "(punto N de la Política de Privacidad)" porque este texto no tiene punto 8.
export const PROVEEDORES = [
  {
    servicio: "Infraestructura en la nube",
    proveedor: "Amazon Web Services",
    para: "servidores, base de datos, respaldos y envío de los correos del servicio",
  },
  {
    servicio: "Modelos de lenguaje",
    // Amazon Web Services por la lectura de listas de camas (Bedrock,
    // backend medical_analysis/censo_learner/lista_ia.py; revisor del bump, 20-oct-2026, M1).
    proveedor: "OpenAI y Amazon Web Services",
    para:
      "con OpenAI, generar los borradores a partir del texto que trata el profesional y " +
      "clasificar los mensajes de nuestros contactos comerciales (punto 8 de la Política de " +
      "Privacidad); con Amazon Web Services, leer las listas de pacientes que el profesional " +
      "pega para ordenar sus camas",
  },
  {
    servicio: "Transcripción de voz",
    proveedor: "Deepgram",
    para: "convertir en texto el dictado por voz",
  },
  {
    servicio: "Red y alojamiento web",
    proveedor: "Cloudflare",
    para:
      "publicar la aplicación web, transportar parte del tráfico y medir, sin cookies, las " +
      "visitas a la página de inicio",
  },
  {
    servicio: "Correo electrónico",
    proveedor: "Zoho, Amazon Web Services y Google",
    para: `correo de la empresa, incluido ${PRIVACIDAD_EMAIL}, y comunicaciones contigo`,
  },
  {
    servicio: "Mensajería para avisos internos",
    proveedor: "Telegram",
    para:
      "avisos a nuestro equipo sobre la actividad del servicio, como nuevas solicitudes de " +
      "demo, y avisos internos sobre mensajes recibidos",
  },
  {
    servicio: "Mensajería de Instagram y TikTok",
    proveedor: "ManyChat",
    para: "gestionar los mensajes y comentarios que recibimos en Instagram y TikTok",
  },
];

// Párrafo que sigue a la tabla del punto 4 de la política, copiado tal cual.
export const OTROS_DESTINATARIOS =
  "Además, Google trata como **responsable independiente** los datos del inicio de sesión " +
  "con Google y de la reunión de demo (Google Calendar y Google Meet). Si nos das tu " +
  "teléfono o tu usuario de Instagram, podemos contactarte por WhatsApp o Instagram. Las " +
  "conversaciones por Instagram y WhatsApp las tratan también Instagram y WhatsApp (Meta), y " +
  "las de TikTok, TikTok, como responsables independientes; las de Instagram y TikTok las " +
  "gestiona además ManyChat, por encargo nuestro. No usamos estas vías para comunicar datos " +
  "de pacientes. Los datos de facturación se comunican, cuando corresponde, a nuestros " +
  "asesores contables y a las autoridades tributarias.";

// Anthropic va APARTE de la lista "por encargo nuestro" (decisión de Sebastián,
// 16-oct-2026: el equipo sigue en planes Claude sin acuerdo de tratamiento, así que
// no se puede presentar como encargado). Mismo párrafo que la política, tal cual.
export const HERRAMIENTAS_ANTHROPIC =
  "Usamos además herramientas de Anthropic para desarrollar y mantener el servicio y para " +
  "redactar respuestas a correos que recibimos; procesan lo que nuestro equipo trabaja con " +
  "ellas, que puede incluir datos que tratamos.";

const listaProveedores = () =>
  PROVEEDORES.map((p) => `  - **${p.servicio}** (${p.proveedor}): ${p.para}.`).join("\n");

// `rolPacientes` es la viñeta del rol sobre los datos de pacientes, distinta
// para el profesional (Tipo A) y el interno (Tipo C); `extra` son viñetas
// propias de cada tipo que van después de esa.
export const seccionDatosPersonales = ({ rolPacientes, extra = [], cooperacionPaciente }) =>
  [
    "## 3. Datos personales y privacidad",
    "",
    "- El tratamiento de datos personales se rige por la Ley N° 19.628 sobre Protección de la Vida",
    "  Privada, considerando sus modificaciones por la Ley N° 21.719. El detalle está en la",
    `  [Política de Privacidad](${POLITICA_URL}), que complementa estos Términos.`,
    "- **Tus datos como usuario:** Salud Wild SpA es el Responsable de los datos de tu cuenta, tu",
    "  habilitación y tu perfil, y de los registros técnicos y de uso. No vendemos datos personales.",
    rolPacientes,
    ...extra,
    "- Para prestar y dar soporte al servicio guardamos un registro de cada generación de texto",
    "  (lo que se envía al modelo de lenguaje, el borrador generado y la versión final que dejas),",
    "  que se elimina a los 30 días. No usamos los datos clínicos para entrenar modelos de",
    "  inteligencia artificial. El audio del dictado no lo conservamos.",
    "- Para prestar el servicio, estos proveedores reciben o procesan datos por encargo nuestro:",
    listaProveedores(),
    `- ${OTROS_DESTINATARIOS}`,
    `- ${HERRAMIENTAS_ANTHROPIC}`,
    "- Los proveedores tratan los datos fuera de Chile, principalmente en Estados Unidos, y en otros",
    "  países donde tienen sus centros de datos. Las transferencias se rigen por los términos",
    "  contractuales y de protección de datos del servicio de cada proveedor.",
    "- **Cómo accedes:** con tu correo electrónico y una contraseña, que guardamos solo en forma de",
    "  *hash*, o con tu cuenta de Google. Si entras con Google, guardamos tu correo y tu nombre; no",
    "  recibimos acceso a tu Gmail, contactos, calendario ni Google Drive.",
    "- **Conservación:** tu cuenta y tus documentos de verificación se conservan mientras la cuenta",
    "  esté activa. Si pides eliminarla, la eliminamos de la plataforma, salvo obligación legal de",
    "  conservar algún dato; las copias de respaldo se eliminan según sus ciclos. Los plazos de los",
    "  demás datos, incluida la información clínica del Modo Turno, están en el punto 6 de la",
    "  Política de Privacidad.",
    "- **Tus derechos:** puedes ejercer los derechos de acceso, rectificación, supresión,",
    `  oposición, portabilidad y bloqueo escribiendo a **${PRIVACIDAD_EMAIL}** e indicando el`,
    "  derecho que invocas. Acusaremos recibo y te responderemos por escrito dentro de los plazos",
    "  legales. Si quieres una copia de tus datos antes de eliminar tu cuenta, pídela por el mismo",
    "  canal.",
    cooperacionPaciente,
    "- Si detectamos una vulneración que afecte tus datos personales o la información que tratas",
    "  en la plataforma, te informaremos conforme a la ley.",
  ]
    .filter((linea) => linea !== undefined && linea !== null)
    .join("\n");
